import { useEffect, useState } from 'react';
import {
  DEFAULT_CHECK_STOCK_LAYOUT,
  validateCheckStockLayout,
  type CheckStockLayout,
  type CheckStockLayoutStatus,
} from '@accounting/shared';
import { useUpdateCheckStockLayout } from '../../hooks/useCheckPrinting';
import { ApiError } from '../../lib/api';

function errText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return (err.body as { error?: string } | null)?.error ?? fallback;
  return err instanceof Error ? err.message : fallback;
}

/** Numeric fields are edited as strings so a half-typed "2." doesn't snap to 2. */
type Draft = Record<keyof CheckStockLayout, string | boolean>;

function toDraft(l: CheckStockLayout): Draft {
  return Object.fromEntries(
    Object.entries(l).map(([k, v]) => [k, typeof v === 'boolean' ? v : String(v)]),
  ) as Draft;
}

function fromDraft(d: Draft): CheckStockLayout {
  const num = (k: keyof CheckStockLayout) => Number(d[k]);
  return {
    pageWidthIn: num('pageWidthIn'),
    pageHeightIn: num('pageHeightIn'),
    checksPerSheet: Math.round(num('checksPerSheet')),
    checkWidthIn: num('checkWidthIn'),
    checkHeightIn: num('checkHeightIn'),
    checkLeftIn: num('checkLeftIn'),
    firstCheckTopIn: num('firstCheckTopIn'),
    checkPitchIn: num('checkPitchIn'),
    stubEnabled: Boolean(d.stubEnabled),
    micrBaselineFromBottomIn: num('micrBaselineFromBottomIn'),
    micrRightMarginIn: num('micrRightMarginIn'),
    fontScale: num('fontScale'),
  };
}

/**
 * Where the stock measurements are entered.
 *
 * System-wide by design: one printed sheet carries checks drawn on several
 * different banks, so the paper geometry cannot vary per company.
 */
export function StockLayoutPanel({ status }: { status: CheckStockLayoutStatus }) {
  const save = useUpdateCheckStockLayout();
  const [draft, setDraft] = useState<Draft>(() => toDraft(status.layout));

  useEffect(() => {
    setDraft(toDraft(status.layout));
  }, [status.layout]);

  const set = (k: keyof CheckStockLayout, v: string | boolean) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const candidate = fromDraft(draft);
  const anyNaN = Object.values(candidate).some((v) => typeof v === 'number' && Number.isNaN(v));
  // Validate as you type with the same function the API uses, so the geometry
  // is checked before a sheet of real check stock is spent finding out.
  const problems = anyNaN ? ['Every measurement needs a number.'] : validateCheckStockLayout(candidate);

  const bottomTearoff =
    candidate.pageHeightIn -
    (candidate.firstCheckTopIn + (candidate.checksPerSheet - 1) * candidate.checkPitchIn + candidate.checkHeightIn);
  const sideTearoff = candidate.pageWidthIn - candidate.checkLeftIn - candidate.checkWidthIn;

  return (
    <div className="card" style={{ padding: 20, marginBottom: 16 }}>
      <h2 style={{ fontSize: 15, marginTop: 0 }}>Check stock measurements</h2>
      <p className="muted" style={{ marginTop: 0, fontSize: 12 }}>
        {status.configured ? (
          <>
            Measured {status.updatedAt ? new Date(status.updatedAt).toLocaleDateString() : ''}. One
            setting for the whole system — a single sheet carries checks from several companies, so
            the paper has to be the same for all of them.
          </>
        ) : (
          <>
            <span className="tag warn">not measured yet</span> These are Check Depot CDW162 figures
            from the product page, not off a ruler. Print the calibration sheet, lay it on real
            stock, and correct anything that does not line up before running a live batch.
          </>
        )}
      </p>

      <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 380px', minWidth: 340 }}>
          <FieldGroup title="Sheet">
            <Num label="Sheet width" unit="in" value={draft.pageWidthIn as string} onChange={(v) => set('pageWidthIn', v)} />
            <Num label="Sheet height" unit="in" value={draft.pageHeightIn as string} onChange={(v) => set('pageHeightIn', v)} />
            <Num label="Checks per sheet" value={draft.checksPerSheet as string} onChange={(v) => set('checksPerSheet', v)} step="1" />
          </FieldGroup>

          <FieldGroup title="One check face">
            <Num label="Check width" unit="in" value={draft.checkWidthIn as string} onChange={(v) => set('checkWidthIn', v)} />
            <Num label="Check height" unit="in" value={draft.checkHeightIn as string} onChange={(v) => set('checkHeightIn', v)} />
          </FieldGroup>

          <FieldGroup title="Position on the sheet">
            <Num
              label="Left edge → check"
              unit="in"
              hint="0 if the check spans the sheet"
              value={draft.checkLeftIn as string}
              onChange={(v) => set('checkLeftIn', v)}
            />
            <Num
              label="Top edge → first check"
              unit="in"
              value={draft.firstCheckTopIn as string}
              onChange={(v) => set('firstCheckTopIn', v)}
            />
            <Num
              label="Check top → next check top"
              unit="in"
              hint="same as height unless there is a gap"
              value={draft.checkPitchIn as string}
              onChange={(v) => set('checkPitchIn', v)}
            />
          </FieldGroup>

          <FieldGroup title="MICR line">
            <Num
              label="Baseline above check bottom"
              unit="in"
              hint="standard 0.25, tolerance ±1/16"
              value={draft.micrBaselineFromBottomIn as string}
              onChange={(v) => set('micrBaselineFromBottomIn', v)}
            />
            <Num
              label="Reserved at the right"
              unit="in"
              hint="standard 1.625 — the bank's amount field"
              value={draft.micrRightMarginIn as string}
              onChange={(v) => set('micrRightMarginIn', v)}
            />
          </FieldGroup>

          <FieldGroup title="Printing">
            <Num
              label="Text size"
              hint="1 = normal; lower if the face is crowded"
              value={draft.fontScale as string}
              onChange={(v) => set('fontScale', v)}
              step="0.05"
            />
            <label style={{ display: 'block', fontSize: 13, marginTop: 8 }}>
              <input
                type="checkbox"
                checked={Boolean(draft.stubEnabled)}
                onChange={(e) => set('stubEnabled', e.target.checked)}
              />{' '}
              Print a record stub on the tearoff beside the check
            </label>
          </FieldGroup>
        </div>

        <div style={{ flex: '0 0 auto' }}>
          <StockDiagram layout={candidate} valid={problems.length === 0} />
          <div className="muted" style={{ fontSize: 11, marginTop: 8, maxWidth: 230 }}>
            {problems.length === 0 && (
              <>
                Side tearoff {sideTearoff.toFixed(2)}", bottom tearoff {bottomTearoff.toFixed(2)}".
              </>
            )}
          </div>
        </div>
      </div>

      {problems.length > 0 && (
        <ul className="field-error" style={{ fontSize: 13, marginTop: 12, paddingLeft: 18 }}>
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      {save.error && <p className="field-error">{errText(save.error, 'Could not save.')}</p>}

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button
          className="btn-primary"
          disabled={problems.length > 0 || save.isPending}
          onClick={() => save.mutate(candidate)}
        >
          Save measurements
        </button>
        <button className="btn-secondary" onClick={() => setDraft(toDraft(status.layout))}>
          Revert
        </button>
        <button
          className="btn-secondary"
          onClick={() => setDraft(toDraft(DEFAULT_CHECK_STOCK_LAYOUT))}
          title="Check Depot CDW162 wallet format"
        >
          Reset to CDW162
        </button>
      </div>
    </div>
  );
}

/** A to-scale plan of the sheet, so a wrong number is visible rather than arithmetic. */
function StockDiagram({ layout, valid }: { layout: CheckStockLayout; valid: boolean }) {
  const W = 210;
  if (!valid || !layout.pageWidthIn || !layout.pageHeightIn) {
    return (
      <div
        style={{
          width: W,
          height: W * 1.29,
          border: '1px dashed var(--border)',
          borderRadius: 4,
          display: 'grid',
          placeItems: 'center',
          fontSize: 11,
        }}
        className="muted"
      >
        Fix the measurements to preview
      </div>
    );
  }

  const scale = W / layout.pageWidthIn;
  const H = layout.pageHeightIn * scale;

  return (
    <svg width={W} height={H} style={{ border: '1px solid var(--border)', borderRadius: 4, background: 'var(--surface, transparent)' }}>
      {Array.from({ length: layout.checksPerSheet }).map((_, i) => {
        const top = (layout.firstCheckTopIn + i * layout.checkPitchIn) * scale;
        const h = layout.checkHeightIn * scale;
        const left = layout.checkLeftIn * scale;
        const w = layout.checkWidthIn * scale;
        const band = 0.625 * scale;
        return (
          <g key={i}>
            {layout.stubEnabled && left > 8 && (
              <rect x={1} y={top} width={left - 3} height={h} fill="rgba(120,120,120,0.10)" stroke="#999" strokeDasharray="3 3" strokeWidth={0.7} />
            )}
            <rect x={left} y={top} width={w} height={h} fill="rgba(46,134,171,0.10)" stroke="#2E86AB" strokeWidth={1} />
            {/* MICR clear band along the bottom of each face. */}
            <rect x={left} y={top + h - band} width={w} height={band} fill="rgba(0,112,192,0.18)" stroke="none" />
            <text x={left + 5} y={top + 12} fontSize={8} fill="#2E86AB">
              check {i + 1}
            </text>
            {layout.stubEnabled && left > 30 && (
              <text x={5} y={top + 12} fontSize={7} fill="#888">
                stub
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function FieldGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div
        className="muted"
        style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function Num({
  label,
  unit,
  hint,
  value,
  onChange,
  step = '0.01',
}: {
  label: string;
  unit?: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  step?: string;
}) {
  return (
    <label style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 13, marginBottom: 5 }}>
      <span style={{ flex: '1 1 auto' }}>
        {label}
        {hint && (
          <span className="muted" style={{ marginLeft: 6, fontSize: 11 }}>
            {hint}
          </span>
        )}
      </span>
      <input
        type="number"
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: 84, textAlign: 'right' }}
      />
      <span className="muted" style={{ fontSize: 11, width: 16 }}>
        {unit ?? ''}
      </span>
    </label>
  );
}
